import ApiClient from '../../client';

// Namespaced `OpenSessions` — participant-facing list of sponsor-published sessions.
// Mirrors the MyMeetings module shape (load once, mutate in place on register/unregister)
// rather than the BookSessions one (which is the legacy event-agenda favourites feature).

export const state = () => ({
  items: [],
  loading: false,
  failed: false,
  // Per-session optimistic-update tracking so a row can show a spinner without blocking
  // the whole list.
  pendingIds: []
});

export const getters = {
  getItems: (state) => state.items,
  isLoading: (state) => state.loading,
  failed: (state) => state.failed,
  isPending: (state) => (id) => state.pendingIds.includes(id),
  // Convenience filters consumed by the view.
  myRegisteredItems: (state) => state.items.filter((s) => s.isRegistered),
  myRegisteredCount: (state, g) => g.myRegisteredItems.length
};

export const mutations = {
  setItems(state, items) {
    state.items = items || [];
  },
  setLoading(state, loading) {
    state.loading = loading;
  },
  setFailed(state, failed) {
    state.failed = failed;
  },
  // Patch one session in-place. Used by both the optimistic flip and the server-truth replace.
  patchItem(state, patch) {
    const idx = state.items.findIndex((s) => s.id === patch.id);
    if (idx < 0) return;
    state.items.splice(idx, 1, { ...state.items[idx], ...patch });
  },
  setRegistration(state, { id, isRegistered }) {
    const idx = state.items.findIndex((s) => s.id === id);
    if (idx < 0) return;
    const item = state.items[idx];
    const delta =
      isRegistered === item.isRegistered ? 0 : isRegistered ? 1 : -1;
    const registeredCount = Math.max(0, (item.registeredCount || 0) + delta);
    state.items.splice(idx, 1, {
      ...item,
      isRegistered,
      registeredCount,
      isFull: registeredCount >= item.capacity
    });
  },
  pushPending(state, id) {
    if (!state.pendingIds.includes(id)) state.pendingIds.push(id);
  },
  popPending(state, id) {
    state.pendingIds = state.pendingIds.filter((x) => x !== id);
  }
};

export const actions = {
  async fetchItems({ commit }) {
    commit('setLoading', true);
    commit('setFailed', false);
    try {
      const response = await ApiClient.getOpenSessions();
      commit('setItems', response.data || []);
    } catch (e) {
      commit('setFailed', true);
    } finally {
      commit('setLoading', false);
    }
  },
  // Optimistic: flip isRegistered locally first, then reconcile with server response.
  // Rollback on failure so the UI never lies.
  async register({ commit, state }, sessionId) {
    const before = state.items.find((s) => s.id === sessionId);
    if (!before) return;
    commit('pushPending', sessionId);
    commit('setRegistration', { id: sessionId, isRegistered: true });
    try {
      const response = await ApiClient.registerOpenSession(sessionId);
      if (response?.data) commit('patchItem', response.data);
    } catch (e) {
      // Roll back the optimistic flip.
      commit('setRegistration', {
        id: sessionId,
        isRegistered: before.isRegistered
      });
      throw e;
    } finally {
      commit('popPending', sessionId);
    }
  },
  async unregister({ commit, state }, sessionId) {
    const before = state.items.find((s) => s.id === sessionId);
    if (!before) return;
    commit('pushPending', sessionId);
    commit('setRegistration', { id: sessionId, isRegistered: false });
    try {
      const response = await ApiClient.unregisterOpenSession(sessionId);
      if (response?.data) commit('patchItem', response.data);
    } catch (e) {
      commit('setRegistration', {
        id: sessionId,
        isRegistered: before.isRegistered
      });
      throw e;
    } finally {
      commit('popPending', sessionId);
    }
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
